import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Users, Sparkles, ArrowRight, Lock } from "lucide-react";
import TopBar from "@/components/TopBar";
import CollectiveGiftModal from "@/components/CollectiveGiftModal";
import FrozenBanner from "@/components/FrozenBanner";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";

export default function CollectiveChoice() {
  const { code } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [groupCode, setGroupCode] = useState("");
  const [joinError, setJoinError] = useState("");
  const [joinSubmitting, setJoinSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/events/${code}`)
      .then((r) => setEvent(r.data.event))
      .catch(() => setEvent(null));
  }, [code]);

  const frozen = !!event?.frozen;

  const submitJoin = async (e) => {
    e.preventDefault();
    setJoinError("");
    const normCode = groupCode.toUpperCase().replace(/[^A-Z0-9]/g, "").trim();
    if (normCode.length < 4) {
      setJoinError(t("collectiveChoice.joinError"));
      return;
    }
    setJoinSubmitting(true);
    try {
      const { data } = await api.get(`/public/reservations/${normCode}`);
      if (!data?.is_active || !data?.product?.id) {
        setJoinError(t("collectiveChoice.joinError"));
        return;
      }
      setJoinOpen(false);
      setGroupCode("");
      navigate(
        `/event/${code}/shop/product/${data.product.id}?join=${normCode}`,
      );
    } catch (e) {
      setJoinError(
        formatApiError(e.response?.data?.detail) ||
          t("collectiveChoice.joinError"),
      );
    } finally {
      setJoinSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF9FB] pb-24" data-testid="collective-choice-page">
      <TopBar />
      <div className="max-w-2xl mx-auto px-5 pt-6">
        <button
          onClick={() => navigate(`/event/${code}`)}
          data-testid="collective-choice-back"
          className="inline-flex items-center gap-2 text-sm text-[#5C4664] hover:text-[#2A0E3C]"
        >
          <ArrowLeft className="w-4 h-4" /> {t("common.back")}
        </button>

        <div className="mt-6 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#3FA535]">
            {t("actions.collective.title")}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C] mt-2 leading-tight">
            {t("collectiveChoice.title")}
          </h1>
          <p className="mt-3 text-[#5C4664] text-sm sm:text-base leading-relaxed">
            {t("collectiveChoice.sub")}
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Create a group */}
          <button
            data-testid="collective-create-btn"
            onClick={() => !frozen && setCreateOpen(true)}
            disabled={frozen}
            aria-disabled={frozen}
            className={`group relative bg-white rounded-3xl p-6 sm:p-7 border border-[#F2E2EC] text-left overflow-hidden transition-all ${
              frozen
                ? "opacity-50 cursor-not-allowed"
                : "hover:border-[#3FA535]/50 hover:shadow-xl hover:-translate-y-0.5"
            }`}
          >
            <div className="w-12 h-12 rounded-2xl bg-[#E8F6E5] text-[#3FA535] flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6" strokeWidth={2.2} />
            </div>
            <div className="font-display text-lg sm:text-xl font-black text-[#2A0E3C] leading-tight">
              {t("collectiveChoice.createTitle")}
            </div>
            <div className="mt-2 text-xs sm:text-sm text-[#5C4664] leading-snug">
              {t("collectiveChoice.createDesc")}
            </div>
            <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#3FA535]">
              {t("common.next")} <ArrowRight className="w-3.5 h-3.5" />
            </div>
            {frozen && (
              <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-[#FCE7F3] text-[#D81B8C] flex items-center justify-center">
                <Lock className="w-3.5 h-3.5" />
              </div>
            )}
            {!frozen && (
              <div
                className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  background: `radial-gradient(circle at top right, #3FA53525, transparent 70%)`,
                }}
              />
            )}
          </button>

          {/* Join an existing pot */}
          <button
            data-testid="collective-join-btn"
            onClick={() => !frozen && setJoinOpen(true)}
            disabled={frozen}
            aria-disabled={frozen}
            className={`group relative bg-white rounded-3xl p-6 sm:p-7 border border-[#F2E2EC] text-left overflow-hidden transition-all ${
              frozen
                ? "opacity-50 cursor-not-allowed"
                : "hover:border-[#D81B8C]/50 hover:shadow-xl hover:-translate-y-0.5"
            }`}
          >
            <div className="w-12 h-12 rounded-2xl bg-[#FCE7F3] text-[#D81B8C] flex items-center justify-center mb-4">
              <Users className="w-6 h-6" strokeWidth={2.2} />
            </div>
            <div className="font-display text-lg sm:text-xl font-black text-[#2A0E3C] leading-tight">
              {t("collectiveChoice.joinTitle")}
            </div>
            <div className="mt-2 text-xs sm:text-sm text-[#5C4664] leading-snug">
              {t("collectiveChoice.joinDesc")}
            </div>
            <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#D81B8C]">
              {t("common.next")} <ArrowRight className="w-3.5 h-3.5" />
            </div>
            {frozen && (
              <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-[#FCE7F3] text-[#D81B8C] flex items-center justify-center">
                <Lock className="w-3.5 h-3.5" />
              </div>
            )}
            {!frozen && (
              <div
                className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  background: `radial-gradient(circle at top right, #D81B8C25, transparent 70%)`,
                }}
              />
            )}
          </button>
        </div>

        <FrozenBanner event={event} />
      </div>

      {/* Create-a-group modal (reuses the existing form) */}
      <CollectiveGiftModal open={createOpen} onOpenChange={setCreateOpen} />

      {/* Join-an-existing-pot drawer */}
      <Drawer open={joinOpen} onOpenChange={setJoinOpen}>
        <DrawerContent className="bg-white max-h-[92vh]">
          <div className="max-w-md mx-auto w-full px-5 pb-8">
            <DrawerHeader className="px-0">
              <div className="w-12 h-12 rounded-xl bg-[#FCE7F3] text-[#D81B8C] flex items-center justify-center mb-2">
                <Users className="w-5 h-5" />
              </div>
              <DrawerTitle className="font-display text-2xl font-black text-[#2A0E3C]">
                {t("collectiveChoice.joinDrawerTitle")}
              </DrawerTitle>
              <DrawerDescription className="text-[#5C4664]">
                {t("collectiveChoice.joinDrawerSub")}
              </DrawerDescription>
            </DrawerHeader>

            <form onSubmit={submitJoin} className="space-y-5">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
                  {t("collectiveChoice.joinCodeLabel")}
                </Label>
                <Input
                  data-testid="join-existing-code-input"
                  value={groupCode}
                  onChange={(e) =>
                    setGroupCode(
                      e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, "")
                        .slice(0, 4),
                    )
                  }
                  placeholder="ABCD"
                  maxLength={4}
                  className="mt-1 h-14 text-center font-mono text-3xl font-bold tracking-[0.4em] rounded-xl border-[#F2E2EC] focus-visible:ring-[#D81B8C]"
                />
              </div>

              {joinError && (
                <p
                  data-testid="join-existing-error"
                  className="text-sm text-[#D81B8C] font-medium"
                >
                  {joinError}
                </p>
              )}

              <Button
                type="submit"
                disabled={joinSubmitting}
                data-testid="join-existing-submit"
                className="w-full rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white h-12 font-semibold gap-2 disabled:opacity-60"
              >
                {t("collectiveChoice.joinCta")}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
